import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Calendar, ChevronRight, ChevronLeft, X, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useLanguage } from '../context/LanguageContext';

// Project data - 1 project per service with multiple images as carousel
const projectsData = [
  {
    id: 1,
    service: 'Fiber Optic',
    serviceEs: 'Fibra Óptica',
    quoteServiceId: 'fiber',
    title: 'Fiber Optic Installation',
    titleEs: 'Instalación de Fibra Óptica',
    location: 'Miami, FL',
    date: '2025',
    images: [
      { src: '/projects/project-17.jpeg', watermark: 'Fiber Optic Installation' },
      { src: '/projects/project-18.jpeg', watermark: 'Fiber Fusion Splicing' },
      { src: '/projects/project-15.jpeg', watermark: 'OTDR Fiber Testing' },
      { src: '/projects/project-24.jpeg', watermark: 'Fiber Cable Run' },
      { src: '/projects/project-32.jpeg', watermark: 'Fiber Termination' },
      { src: '/projects/project-37.jpeg', watermark: 'Fiber Distribution Panel' },
    ]
  },
  {
    id: 2,
    service: 'CCTV',
    serviceEs: 'CCTV / Videovigilancia',
    quoteServiceId: 'cctv',
    title: 'Security Camera System',
    titleEs: 'Sistema de Cámaras de Seguridad',
    location: 'Orlando, FL',
    date: '2025',
    images: [
      { src: '/projects/project-22.jpeg', watermark: 'Commercial CCTV Installation' },
      { src: '/projects/project-23.jpeg', watermark: 'IP Camera System' },
      { src: '/projects/project-33.jpeg', watermark: 'Security Camera Setup' },
      { src: '/projects/project-39.jpeg', watermark: 'NVR Recorder Installation' },
      { src: '/projects/project-5.jpeg', watermark: '4K Surveillance System' },
      { src: '/projects/project-12.jpeg', watermark: 'Dome Camera Installation' },
    ]
  },
  {
    id: 3,
    service: 'Structured Cabling',
    serviceEs: 'Cableado Estructurado',
    quoteServiceId: 'cabling',
    title: 'Cat6A Network Cabling',
    titleEs: 'Cableado de Red Cat6A',
    location: 'Tampa, FL',
    date: '2026',
    images: [
      { src: '/projects/project-1.jpeg', watermark: 'Cat6A Cable Installation' },
      { src: '/projects/project-3.jpeg', watermark: 'Structured Cabling System' },
      { src: '/projects/project-9.jpeg', watermark: 'Patch Panel Termination' },
      { src: '/projects/project-14.jpeg', watermark: 'Cable Management Rack' },
      { src: '/projects/project-19.jpeg', watermark: 'Network Drop Installation' },
      { src: '/projects/project-25.jpeg', watermark: 'Cat6 UTP Termination' },
      { src: '/projects/project-30.jpeg', watermark: 'Cable Tray Installation' },
    ]
  },
  {
    id: 4,
    service: 'Access Control',
    serviceEs: 'Control de Acceso',
    quoteServiceId: 'access',
    title: 'Access Control System',
    titleEs: 'Sistema de Control de Acceso',
    location: 'Jacksonville, FL',
    date: '2025',
    images: [
      { src: '/projects/project-34.jpeg', watermark: 'Access Control Panel' },
      { src: '/projects/project-26.jpeg', watermark: 'Card Reader Installation' },
      { src: '/projects/project-7.jpeg', watermark: 'Biometric Access System' },
      { src: '/projects/project-36.jpeg', watermark: 'Door Access Controller' },
      { src: '/projects/project-40.jpeg', watermark: 'Security Access Point' },
      { src: '/projects/project-2.jpeg', watermark: 'Electronic Lock System' },
    ]
  },
  {
    id: 5,
    service: 'Networking',
    serviceEs: 'Redes / Networking',
    quoteServiceId: 'network',
    title: 'Enterprise Network Setup',
    titleEs: 'Configuración de Red Empresarial',
    location: 'West Palm Beach, FL',
    date: '2025',
    images: [
      { src: '/projects/project-28.jpeg', watermark: 'Network Rack Installation' },
      { src: '/projects/project-21.jpeg', watermark: 'Network Switch Setup' },
      { src: '/projects/project-35.jpeg', watermark: 'WiFi Access Point Deploy' },
      { src: '/projects/project-27.jpeg', watermark: 'Router Configuration' },
      { src: '/projects/project-38.jpeg', watermark: 'PoE Switch Installation' },
      { src: '/projects/project-13.jpeg', watermark: 'Enterprise Network Cabling' },
    ]
  },
  {
    id: 6,
    service: 'Data Center',
    serviceEs: 'Centro de Datos',
    quoteServiceId: 'cabling',
    title: 'Data Center Infrastructure',
    titleEs: 'Infraestructura de Centro de Datos',
    location: 'Panama City, FL',
    date: '2025',
    images: [
      { src: '/projects/project-8.jpeg', watermark: 'Data Center Build' },
      { src: '/projects/project-20.jpeg', watermark: 'Server Room Setup' },
      { src: '/projects/project-29.jpeg', watermark: 'Server Rack Installation' },
      { src: '/projects/project-10.jpeg', watermark: 'Data Center Power System' },
      { src: '/projects/project-16.jpeg', watermark: 'Server Cooling System' },
      { src: '/projects/project-6.jpeg', watermark: 'Server Cable Management' },
    ]
  }
];

// Image Carousel Component for each project
function ProjectCarousel({ project, isEs, onOpenModal }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const images = project.images;

  const nextImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <Card className="bg-white border border-gray-200 hover:shadow-xl transition-all duration-300 overflow-hidden group">
      {/* Image with Carousel */}
      <div 
        className="relative aspect-[4/3] overflow-hidden cursor-pointer"
        onClick={() => onOpenModal(project, currentIndex)}
      >
        <img 
          src={images[currentIndex].src}
          alt={`${images[currentIndex].watermark} - ${project.service} project in ${project.location} by IQES Low Voltage`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        
        {/* Watermark - Subtle corner style */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
          <span className="text-white text-xs font-semibold tracking-wide">
            {images[currentIndex].watermark}
          </span>
        </div>

        {/* Navigation Arrows */}
        <button
          onClick={prevImage}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Previous image"
        >
          <ChevronLeft className="w-5 h-5 text-gray-800" />
        </button>
        <button
          onClick={nextImage}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
          aria-label="Next image"
        >
          <ChevronRight className="w-5 h-5 text-gray-800" />
        </button>

        {/* Image Counter */}
        <div className="absolute top-3 right-3 bg-black/60 text-white text-xs px-2 py-1 rounded-full">
          {currentIndex + 1} / {images.length}
        </div>

        {/* Service Badge */}
        <div className="absolute top-3 left-3">
          <span className="text-white text-xs font-bold tracking-wider uppercase px-2 py-1 rounded" style={{ backgroundColor: '#00A651' }}>
            {isEs ? project.serviceEs : project.service}
          </span>
        </div>
      </div>

      {/* Project Info */}
      <CardContent className="p-4">
        <h3 className="text-gray-800 font-semibold text-base mb-2">
          {isEs ? project.titleEs : project.title}
        </h3>
        <div className="flex items-center justify-between text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <MapPin className="w-4 h-4" />
            {project.location}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {project.date}
          </span>
        </div>
        
        {/* Thumbnail Dots */}
        <div className="flex justify-center gap-1.5 mt-3">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-2 h-2 rounded-full transition-colors ${
                idx === currentIndex ? 'bg-[#00A651]' : 'bg-gray-300 hover:bg-gray-400'
              }`}
              aria-label={`Go to image ${idx + 1}`}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

// Full Screen Modal for viewing images
function ImageModal({ project, initialIndex, onClose, isEs }) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const images = project.images;

  const nextImage = () => {
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/90"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="relative max-w-5xl w-full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute -top-12 right-0 p-2 rounded-full bg-white/20 text-white hover:bg-white/30 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Main Image */}
        <div className="relative">
          <img
            src={images[currentIndex].src}
            alt={`${images[currentIndex].watermark} - Professional ${project.service} installation in ${project.location} by IQES Low Voltage contractor`}
            className="w-full h-auto max-h-[75vh] object-contain rounded-lg"
          />
          
          {/* Watermark */}
          <div className="absolute bottom-4 left-4 bg-black/70 text-white px-4 py-2 rounded-lg">
            <p className="text-sm font-semibold">{images[currentIndex].watermark}</p>
            <p className="text-xs text-white/70">{isEs ? project.titleEs : project.title} - {project.location}</p>
          </div>

          {/* Navigation */}
          <button
            onClick={prevImage}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg"
          >
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <button
            onClick={nextImage}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow-lg"
          >
            <ChevronRight className="w-6 h-6 text-gray-800" />
          </button>
        </div>

        {/* Thumbnails */}
        <div className="flex justify-center gap-2 mt-4 overflow-x-auto pb-2">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentIndex ? 'border-[#00A651] scale-110' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img 
                src={img.src} 
                alt={`${project.service} thumbnail ${idx + 1} - ${project.location}`} 
                className="w-full h-full object-cover" 
              />
            </button>
          ))}
        </div>

        {/* Counter and Quote Button */}
        <div className="flex items-center justify-between mt-4 px-4">
          <p className="text-white/70 text-sm">
            {currentIndex + 1} / {images.length}
          </p>
          <Link to={`/quote?service=${project.quoteServiceId}`}>
            <Button 
              className="text-white flex items-center gap-2"
              style={{ backgroundColor: '#00A651' }}
            >
              <FileText className="w-4 h-4" />
              {isEs ? 'Cotizar Este Servicio' : 'Quote This Service'}
            </Button>
          </Link>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function Projects() {
  const { language } = useLanguage();
  const isEs = language === 'es';
  const [modalData, setModalData] = useState(null);

  const openModal = (project, index) => {
    setModalData({ project, index });
  };

  const closeModal = () => {
    setModalData(null);
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero */}
      <section className="py-10" style={{ background: 'linear-gradient(135deg, rgb(0, 51, 102) 0%, rgba(0, 51, 102, 0.867) 50%, rgba(0, 166, 81, 0.25) 100%)' }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">
            {isEs ? 'Nuestros Proyectos' : 'Our Projects'}
          </h1>
          <p className="text-white/70 text-sm max-w-2xl mx-auto">
            {isEs 
              ? 'Explore nuestra galería de proyectos completados en toda Florida. Más de 280 proyectos exitosos en instalaciones de bajo voltaje.'
              : 'Explore our gallery of completed projects across Florida. Over 280 successful low voltage installation projects.'}
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-8 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-8 text-center">
            <div>
              <p className="text-3xl font-bold" style={{ color: '#003366' }}>280+</p>
              <p className="text-sm text-gray-600">{isEs ? 'Proyectos Completados' : 'Completed Projects'}</p>
            </div>
            <div>
              <p className="text-3xl font-bold" style={{ color: '#00A651' }}>6</p>
              <p className="text-sm text-gray-600">{isEs ? 'Ciudades en Florida' : 'Cities in Florida'}</p>
            </div>
            <div>
              <p className="text-3xl font-bold" style={{ color: '#003366' }}>13+</p>
              <p className="text-sm text-gray-600">{isEs ? 'Años de Experiencia' : 'Years Experience'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Grid - 1 per service */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-xl font-bold text-center mb-8" style={{ color: '#003366' }}>
            {isEs ? 'Proyectos por Servicio' : 'Projects by Service'}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {projectsData.map((project) => (
              <motion.div
                key={project.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: project.id * 0.1 }}
              >
                <ProjectCarousel 
                  project={project} 
                  isEs={isEs}
                  onOpenModal={openModal}
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10" style={{ backgroundColor: '#003366' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-2">
            {isEs ? '¿Tiene un proyecto en mente?' : 'Have a project in mind?'}
          </h2>
          <p className="text-white/70 text-sm mb-4 max-w-xl mx-auto">
            {isEs 
              ? 'Contáctenos hoy para una cotización personalizada.'
              : 'Contact us today for a personalized quote.'}
          </p>
          <Link to="/quote">
            <Button className="text-white" style={{ backgroundColor: '#00A651' }}>
              {isEs ? 'Obtener Cotización' : 'Get Quote'}
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Modal */}
      <AnimatePresence>
        {modalData && (
          <ImageModal
            project={modalData.project}
            initialIndex={modalData.index}
            onClose={closeModal}
            isEs={isEs}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
